import {
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../components/layout/headerActionStyles";
import {
  Activity,
  AlertCircle,
  ArrowRight,
  BarChart3,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Info,
  Layers,
  Sparkles,
  TrendingUp,
  Zap,
  RefreshCw,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, PageLayout, SectionHeader, Tooltip } from "../components";
import { DashboardSkeleton } from "../components/skeletons";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError } from "../shared/alertService";
import apiService from "../shared/apiService";
import { API_ENDPOINTS } from "../shared/constants";
import { cn } from "../utils/cn";
import { getPlatformConfig, getPlatformName } from "../utils/platformIcons";

interface PlatformData {
  id: string;
  platform: string;
  count: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  hoverBg: string;
}

interface ActivityItem {
  action: string;
  action_key?: string;
  platform?: string;
  time: string;
  time_seconds?: number;
  status: "success" | "error" | "pending" | "info";
}

interface DashboardStats {
  total_posts: number;
  scheduled: number;
  published: number;
  errors: number;
  platforms: PlatformData[];
  recent_activity: ActivityItem[];
  today: {
    published: number;
    scheduled: number;
    pending: number;
  };
}

const KPI_COLORS: Record<
  string,
  {
    border: string;
    text: string;
    iconBg: string;
    iconText: string;
    hoverGlow: string;
    subBg: string;
    subText: string;
  }
> = {
  purple: {
    border: "border-purple-200",
    text: "text-purple-700",
    iconBg: "bg-purple-100",
    iconText: "text-purple-600",
    hoverGlow: "hover:shadow-[0_4px_20px_rgba(168,85,247,0.15)]",
    subBg: "bg-purple-50",
    subText: "text-purple-600",
  },
  amber: {
    border: "border-amber-200",
    text: "text-amber-700",
    iconBg: "bg-amber-100",
    iconText: "text-amber-600",
    hoverGlow: "hover:shadow-[0_4px_20px_rgba(245,158,11,0.15)]",
    subBg: "bg-amber-50",
    subText: "text-amber-600",
  },
  emerald: {
    border: "border-emerald-200",
    text: "text-emerald-700",
    iconBg: "bg-emerald-100",
    iconText: "text-emerald-600",
    hoverGlow: "hover:shadow-[0_4px_20px_rgba(16,185,129,0.15)]",
    subBg: "bg-emerald-50",
    subText: "text-emerald-600",
  },
  rose: {
    border: "border-rose-200",
    text: "text-rose-700",
    iconBg: "bg-rose-100",
    iconText: "text-rose-600",
    hoverGlow: "hover:shadow-[0_4px_20px_rgba(244,63,94,0.15)]",
    subBg: "bg-rose-50",
    subText: "text-rose-600",
  },
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { activeContext } = useActiveContext();
  const { t, language } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.get(API_ENDPOINTS.POSTS.STATS);

      if (data.success && data.stats) {
        const totalPosts =
          (data.stats.pending || 0) +
          (data.stats.approved || 0) +
          (data.stats.published || 0) +
          (data.stats.rejected || 0) +
          (data.stats.scheduled || 0);

        const getPlatformMap = (platform: string) => {
          const config = getPlatformConfig(platform);
          return {
            color: config.color,
            bgColor: config.bgColor,
            hoverBg: config.hoverBg,
          };
        };

        const platforms: PlatformData[] = Object.entries(
          data.stats.platforms || {},
        ).map(([platform, count]) => {
          const platformInfo = getPlatformMap(platform);
          const config = getPlatformConfig(platform);
          const IconComponent = config.icon;
          return {
            id: platform,
            platform: getPlatformName(platform),
            count: count as number,
            icon: <IconComponent className={cn("h-4 w-4", config.color)} />,
            ...platformInfo,
          };
        });

        setStats({
          total_posts: totalPosts,
          scheduled: data.stats.scheduled || 0,
          published: data.stats.published || 0,
          errors: data.stats.rejected || 0,
          platforms: platforms,
          recent_activity: data.stats.recent_activity || [],
          today: data.stats.today || {
            published: 0,
            scheduled: 0,
            pending: 0,
          },
        });
      } else {
        throw new Error("Resposta inválida do servidor");
      }
    } catch (error: any) {
      console.error("Erro ao buscar estatísticas:", error);
      showToastError(error.message || t("Erro ao carregar dados do dashboard"));
      setStats({
        total_posts: 0,
        scheduled: 0,
        published: 0,
        errors: 0,
        platforms: [],
        recent_activity: [],
        today: {
          published: 0,
          scheduled: 0,
          pending: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStats();
  }, [
    activeContext.type,
    activeContext.organization?.id,
    activeContext.clientCompany?.id,
    fetchStats,
  ]);

  const kpis = useMemo(
    () => [
      {
        title: t("Total de Posts"),
        value: stats?.total_posts ?? 0,
        icon: FileText,
        color: "purple",
        subtitle: t("Todos os tempos"),
        tooltip: t(
          "Volume total acumulado de publicações e agendamentos processados pela plataforma.",
        ),
        onClick: () => navigate("/posts"),
      },
      {
        title: t("Agendados"),
        value: stats?.scheduled ?? 0,
        icon: Clock,
        color: "amber",
        subtitle: t("Próximas 24h"),
        tooltip: t(
          "Publicações programadas na fila para postagem automática nas próximas 24 horas.",
        ),
        onClick: () => navigate("/posts/calendar"),
      },
      {
        title: t("Publicados"),
        value: stats?.published ?? 0,
        icon: CheckCircle,
        color: "emerald",
        subtitle: t("Últimas 24h"),
        tooltip: t(
          "Publicações disparadas com sucesso nas redes sociais conectadas.",
        ),
        onClick: () => navigate("/posts"),
      },
      {
        title: t("Erros na Fila"),
        value: stats?.errors ?? 0,
        icon: AlertCircle,
        color: "rose",
        subtitle: t("Rejeitados / Falhas"),
        tooltip: t(
          "Publicações rejeitadas ou com falhas de entrega que requerem sua atenção.",
        ),
        onClick: () => navigate("/posts"),
      },
    ],
    [stats, t, navigate],
  );

  return (
    <PageLayout
      title={t("Dashboard")}
      subtitle={t(
        "Métricas em tempo real: desempenho, engajamento e performance das suas redes sociais.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2! py-2!"
      loading={loading}
      skeleton={<DashboardSkeleton />}
      actions={
        <Button
          variant="outline"
          size="sm"
          icon={
            <RefreshCw
              className={cn(HEADER_SECONDARY_ICON, loading && "animate-spin")}
            />
          }
          onClick={fetchStats}
          disabled={loading}
          className={HEADER_SECONDARY_BUTTON}
        >
          <span className="text-xs">{t("Atualizar")}</span>
        </Button>
      }
    >
      <div className="w-full flex flex-col gap-2">
        {/* ========================================================================= */}
        {/* BANDA 1: Header de Controle & Visão Geral Executiva (4 KPIs)               */}
        {/* ========================================================================= */}
        <div className="shrink-0 flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
          <SectionHeader
            icon={<TrendingUp className="h-5 w-5" />}
            title={t("Visão Geral Executiva")}
            subtitle={t("Métricas Globais e Distribuição")}
            iconBgClass="bg-purple-600"
            action={
              <div className="flex items-center gap-2 self-center flex-wrap">
                <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-50 border border-green-200">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[10px] font-black text-green-700 uppercase tracking-wider">
                    {t("Sincronizado")}
                  </span>
                </div>
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-50 border border-purple-100 text-purple-700 text-xs font-medium">
                  <Clock className="h-3 w-3 text-purple-600" />
                  <span>
                    {t("Atualizado:")}{" "}
                    {new Date().toLocaleTimeString(
                      language === "en" ? "en-US" : "pt-BR",
                      { hour: "2-digit", minute: "2-digit" },
                    )}
                  </span>
                </div>
              </div>
            }
          />

          {/* Grid de 4 KPIs com mesma altura e proporção rigorosa */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {kpis.map((kpi) => {
              const s = KPI_COLORS[kpi.color];
              const Icon = kpi.icon;
              return (
                <div
                  key={kpi.title}
                  onClick={kpi.onClick}
                  className={cn(
                    "relative bg-white rounded-xl border p-3.5 shadow-xs transition-all duration-200 flex flex-col justify-between overflow-hidden group min-h-[116px] cursor-pointer hover:-translate-y-0.5",
                    s.border,
                    s.hoverGlow,
                  )}
                >
                  {/* Accent Top Bar */}
                  <div
                    className={cn(
                      "absolute top-0 left-0 right-0 h-[3px]",
                      s.iconBg,
                    )}
                  />

                  <div className="flex items-start justify-between pt-0.5 mb-2">
                    <div className="flex flex-col min-w-0 pr-1">
                      <div className="flex items-center gap-1 mb-1">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest truncate">
                          {kpi.title}
                        </p>
                        {kpi.tooltip && (
                          <Tooltip content={kpi.tooltip} position="top">
                            <Info className="h-3 w-3 text-gray-400 hover:text-purple-600 transition-colors cursor-help shrink-0" />
                          </Tooltip>
                        )}
                      </div>
                      <h3
                        className={cn(
                          "text-2xl sm:text-3xl font-black tracking-tight leading-none tabular-nums",
                          s.text,
                        )}
                      >
                        {kpi.value}
                      </h3>
                    </div>
                    <div
                      className={cn(
                        "w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110",
                        s.iconBg,
                        s.iconText,
                      )}
                    >
                      <Icon className="h-4.5 w-4.5" />
                    </div>
                  </div>

                  <div
                    className={cn(
                      "flex items-center justify-between gap-1 text-[10px] font-semibold px-2 py-1.5 rounded-lg",
                      s.subBg,
                      s.subText,
                    )}
                  >
                    <div className="flex items-center gap-1.5 truncate">
                      <Clock className="h-3 w-3 shrink-0" />
                      <span className="truncate">{kpi.subtitle}</span>
                    </div>
                    <ArrowRight className="h-3 w-3 shrink-0 opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* BANDA 2: Análise de Canais & Atividade Recente (Grid 2 Colunas)             */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 w-full items-stretch">
          {/* Card: Posts por Plataforma */}
          <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs h-full justify-between">
            <div>
              <SectionHeader
                icon={<BarChart3 className="h-5 w-5" />}
                title={t("Posts por Plataforma")}
                subtitle={t("Distribuição de Conteúdo por Canal")}
                iconBgClass="bg-purple-600"
              />

              <div className="space-y-2">
                {stats?.platforms && stats.platforms.length > 0 ? (
                  stats.platforms.map((item) => {
                    const totalP = stats.total_posts || 1;
                    const percentage = Math.min(
                      100,
                      Math.round((item.count / totalP) * 100),
                    );

                    return (
                      <div
                        key={item.id}
                        className={cn(
                          "relative p-2.5 rounded-xl border border-purple-100/80 transition-all duration-200 group overflow-hidden flex flex-col justify-between gap-1.5",
                          item.bgColor,
                          item.hoverBg,
                        )}
                      >
                        <div className="flex items-center justify-between relative z-10">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-lg bg-white shadow-xs flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                              {item.icon}
                            </div>
                            <span
                              className={cn(
                                "text-xs sm:text-sm font-bold truncate",
                                item.color,
                              )}
                            >
                              {item.platform}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[10px] font-semibold text-gray-500">
                              {percentage}%
                            </span>
                            <span className="text-xs sm:text-sm font-black text-gray-900 bg-white/90 border border-purple-100 px-2 py-0.5 rounded-md shadow-2xs tabular-nums">
                              {item.count}
                            </span>
                          </div>
                        </div>

                        {/* Progress Bar visual de proporção */}
                        <div className="w-full bg-black/5 rounded-full h-1 overflow-hidden">
                          <div
                            className="h-full bg-purple-600 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center bg-purple-50/20 rounded-xl border border-dashed border-purple-200">
                    <BarChart3 className="h-8 w-8 text-purple-300 mb-2" />
                    <p className="text-xs font-semibold text-gray-700">
                      {t("Nenhum post registrado")}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {t(
                        "Publicações agendadas ou enviadas aparecerão aqui agrupadas por rede.",
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Card: Atividade Recente */}
          <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs h-full justify-between">
            <div>
              <SectionHeader
                icon={<Activity className="h-5 w-5" />}
                title={t("Atividade Recente")}
                subtitle={t("Últimas Ações e Eventos Registrados")}
                iconBgClass="bg-purple-600"
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<ArrowRight className="h-3 w-3" />}
                    iconPosition="right"
                    className="rounded-lg h-8! min-h-[32px]! py-1! border! border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700! shadow-xs! text-xs font-semibold whitespace-nowrap shrink-0"
                    onClick={() => navigate("/posts")}
                  >
                    {t("Ver Histórico")}
                  </Button>
                }
              />

              <div className="space-y-2">
                {stats?.recent_activity && stats.recent_activity.length > 0 ? (
                  stats.recent_activity.slice(0, 5).map((item, index) => {
                    const statusConfig = {
                      success: {
                        dot: "bg-emerald-500",
                        badge:
                          "bg-emerald-50 text-emerald-700 border-emerald-200",
                      },
                      error: {
                        dot: "bg-rose-500",
                        badge: "bg-rose-50 text-rose-700 border-rose-200",
                      },
                      pending: {
                        dot: "bg-amber-500",
                        badge: "bg-amber-50 text-amber-700 border-amber-200",
                      },
                      info: {
                        dot: "bg-blue-500",
                        badge: "bg-blue-50 text-blue-700 border-blue-200",
                      },
                    }[item.status || "info"];

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between gap-2.5 p-2.5 rounded-xl border border-purple-100/60 bg-purple-50/10 hover:bg-purple-50/40 transition-colors duration-150"
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full shrink-0",
                              statusConfig.dot,
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-gray-900 truncate leading-tight">
                              {(() => {
                                const platform = getPlatformName(
                                  item.platform || "plataforma",
                                );
                                switch (item.action_key) {
                                  case "published":
                                    return t("Post publicado no {platform}", {
                                      platform,
                                    });
                                  case "scheduled":
                                    return t("Post agendado para {platform}", {
                                      platform,
                                    });
                                  case "pending":
                                    return t("Conteúdo processado");
                                  case "rejected":
                                    return t("Erro na publicação {platform}", {
                                      platform,
                                    });
                                  default:
                                    return t(item.action);
                                }
                              })()}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {item.platform && (
                            <span className="hidden sm:inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-white border border-purple-100 text-gray-600 rounded-sm">
                              {getPlatformName(item.platform)}
                            </span>
                          )}
                          <span className="text-[10px] font-semibold text-gray-400 tabular-nums">
                            {item.time_seconds != null
                              ? (() => {
                                  const s = item.time_seconds;
                                  if (s < 60)
                                    return t("{n}s atrás", {
                                      n: Math.floor(s),
                                    });
                                  if (s < 3600)
                                    return t("{n}m atrás", {
                                      n: Math.floor(s / 60),
                                    });
                                  if (s < 86400)
                                    return t("{n}h atrás", {
                                      n: Math.floor(s / 3600),
                                    });
                                  const days = Math.floor(s / 86400);
                                  return days === 1
                                    ? t("1 dia atrás")
                                    : t("{n} dias atrás", { n: days });
                                })()
                              : t(item.time)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center bg-purple-50/20 rounded-xl border border-dashed border-purple-200">
                    <Activity className="h-8 w-8 text-purple-300 mb-2" />
                    <p className="text-xs font-semibold text-gray-700">
                      {t("Nenhuma atividade recente")}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {t(
                        "Eventos de agendamento e publicação aparecerão em tempo real.",
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* BANDA 3: Ações Rápidas & Resumo do Dia (Grid 2 Colunas)                     */}
        {/* ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 w-full items-stretch">
          {/* Card: Ações Rápidas */}
          <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs h-full justify-between">
            <div>
              <SectionHeader
                icon={<Zap className="h-5 w-5" />}
                title={t("Ações Rápidas")}
                subtitle={t("Atalhos Diretos de Produtividade")}
                iconBgClass="bg-purple-600"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  {
                    title: t("Agendar Post"),
                    desc: t("Programar publicação"),
                    icon: Calendar,
                    path: "/posts/calendar",
                    border: "border-purple-200 hover:border-purple-300",
                    bg: "bg-purple-50/40 hover:bg-purple-100/60",
                    iconBg: "bg-purple-600 text-white",
                    textColor: "text-purple-950",
                  },
                  {
                    title: t("Criar Conteúdo"),
                    desc: t("Biblioteca e IA"),
                    icon: FileText,
                    path: "/content",
                    border: "border-emerald-200 hover:border-emerald-300",
                    bg: "bg-emerald-50/40 hover:bg-emerald-100/60",
                    iconBg: "bg-emerald-600 text-white",
                    textColor: "text-emerald-950",
                  },
                  {
                    title: t("Ver Analytics"),
                    desc: t("Métricas de canais"),
                    icon: BarChart3,
                    path: "/analytics",
                    border: "border-blue-200 hover:border-blue-300",
                    bg: "bg-blue-50/40 hover:bg-blue-100/60",
                    iconBg: "bg-blue-600 text-white",
                    textColor: "text-blue-950",
                  },
                  {
                    title: t("Monitorar IA"),
                    desc: t("Status do assistente"),
                    icon: Sparkles,
                    path: "/analytics",
                    border: "border-amber-200 hover:border-amber-300",
                    bg: "bg-amber-50/40 hover:bg-amber-100/60",
                    iconBg: "bg-amber-600 text-white",
                    textColor: "text-amber-950",
                  },
                ].map((action, idx) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() => navigate(action.path)}
                      className={cn(
                        "group relative p-3 rounded-xl border transition-all duration-200 flex items-center justify-between text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-500 shadow-2xs hover:shadow-xs hover:-translate-y-0.5",
                        action.bg,
                        action.border,
                      )}
                      aria-label={action.title}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 shadow-2xs group-hover:scale-105 transition-transform",
                            action.iconBg,
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p
                            className={cn(
                              "text-xs sm:text-sm font-bold truncate leading-tight",
                              action.textColor,
                            )}
                          >
                            {action.title}
                          </p>
                          <p className="text-[10px] text-gray-400 font-medium truncate mt-0.5">
                            {action.desc}
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-0.5 transition-all shrink-0 ml-1" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Card: Resumo do Dia */}
          <div className="flex flex-col bg-white rounded-xl border border-purple-200 p-3 shadow-xs h-full justify-between">
            <div>
              <SectionHeader
                icon={<Clock className="h-5 w-5" />}
                title={t("Resumo do Dia")}
                subtitle={t("Indicadores das Últimas 24 Horas")}
                iconBgClass="bg-purple-600"
              />

              <div className="space-y-2">
                {[
                  {
                    label: t("Posts agendados hoje"),
                    value: stats?.today.scheduled || 0,
                    icon: Calendar,
                    iconColor: "text-blue-600",
                    iconBg: "bg-blue-100",
                    rowBg: "bg-blue-50/20 hover:bg-blue-50/50",
                    border: "border-blue-100",
                    valueColor: "text-blue-700",
                  },
                  {
                    label: t("Posts publicados"),
                    value: stats?.today.published || 0,
                    icon: CheckCircle,
                    iconColor: "text-emerald-600",
                    iconBg: "bg-emerald-100",
                    rowBg: "bg-emerald-50/20 hover:bg-emerald-50/50",
                    border: "border-emerald-100",
                    valueColor: "text-emerald-700",
                  },
                  {
                    label: t("Pendentes aprovação"),
                    value: stats?.today.pending || 0,
                    icon: Clock,
                    iconColor: "text-amber-600",
                    iconBg: "bg-amber-100",
                    rowBg: "bg-amber-50/20 hover:bg-amber-50/50",
                    border: "border-amber-100",
                    valueColor: "text-amber-700",
                  },
                  {
                    label: t("Conteúdos processados"),
                    value: stats?.total_posts || 0,
                    icon: Layers,
                    iconColor: "text-purple-600",
                    iconBg: "bg-purple-100",
                    rowBg: "bg-purple-50/20 hover:bg-purple-50/50",
                    border: "border-purple-100",
                    valueColor: "text-purple-700",
                  },
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={idx}
                      className={cn(
                        "flex items-center justify-between p-2.5 rounded-xl border transition-colors duration-150",
                        item.rowBg,
                        item.border,
                      )}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className={cn(
                            "w-7 h-7 rounded-lg flex items-center justify-center shrink-0",
                            item.iconBg,
                            item.iconColor,
                          )}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <span className="text-xs sm:text-sm font-semibold text-gray-700 truncate">
                          {item.label}
                        </span>
                      </div>
                      <span
                        className={cn(
                          "text-sm sm:text-base font-black tabular-nums shrink-0 ml-2",
                          item.valueColor,
                        )}
                      >
                        {item.value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Dashboard;
